'use client';

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';
import { api, ApiError } from '@/lib/api/client';

/**
 * The operations screen: what needs attention, and how the business is doing.
 *
 * The version this replaces was sparse by design and wrong for the job. Two
 * alert banners the height of a hand, four lonely numbers, and a large empty
 * box announcing "Nothing on the road" -- then a scroll to find anything else.
 * There were no trends, no comparisons, and nothing to see whether today was
 * good or bad.
 *
 * Operational dashboards are not executive summaries. An operator needs to
 * read several streams at once, and an airy layout forces them to scan
 * sequentially instead -- which is the parallel-processing advantage the
 * screen exists to give them. So this is deliberately dense, in zones:
 * alerts across the top, today's numbers against the same hours yesterday,
 * the fortnight's shape in the middle, live work and recent events beside it.
 *
 * Colour carries meaning only. Amber is something waiting; red is something
 * wrong; everything else is quiet.
 */

interface Overview {
  jobsToday: {
    total: number; delivered: number; inFlight: number;
    searching: number; cancelled: number; failed: number;
  };
  revenueToday: { currencyCode: string; totalMinor?: number; total_minor?: number };
  drivers: {
    online: number; onJob: number; onBreak: number;
    offline: number; approved: number;
  };
  documents: { awaitingReview: number };
  owedToDrivers: { currencyCode: string; totalMinor?: number; total_minor?: number }[];
  unpaidDelivered?: number;
}

interface Finance {
  totals: { currency_code: string; charged_minor: string; commission_minor: string }[];
  daily: {
    day: string; deliveries: number;
    charged_minor: string; commission_minor: string;
  }[];
}

interface Analytics {
  bookingsByHour: { hour: number; deliveries: number }[];
  drivers: { total: number; dispatchable: number; online: number;
              avg_rating: string | null; ratings: number };
}

interface Activity {
  kind: string; at: string; reference: string;
  detail: string; who: string; actor: string;
}

interface LiveJob {
  id: string; reference: string; state: string;
  driver_name: string | null; total_minor: string; currency_code: string;
}

const money = (minor: string | number | null | undefined, currency = '') =>
  `${currency} ${(Number(minor ?? 0) / 100).toFixed(2)}`.trim();

const ago = (iso: string) => {
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return 'now';
  if (s < 3600) return `${Math.floor(s / 60)}m`;
  if (s < 86400) return `${Math.floor(s / 3600)}h`;
  return `${Math.floor(s / 86400)}d`;
};

export default function DashboardClient() {
  const [overview, setOverview] = useState<Overview | null>(null);
  const [finance, setFinance] = useState<Finance | null>(null);
  const [analytics, setAnalytics] = useState<Analytics | null>(null);
  const [activity, setActivity] = useState<Activity[]>([]);
  const [live, setLive] = useState<LiveJob[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    const to = new Date().toISOString().slice(0, 10);
    const from = new Date(Date.now() - 13 * 86_400_000).toISOString().slice(0, 10);
    try {
      const [o, f, a, act, jobs] = await Promise.all([
        api.get<Overview>('/admin/overview'),
        api.get<Finance>(`/admin/finance?from=${from}&to=${to}`),
        api.get<Analytics>(`/admin/analytics?from=${from}&to=${to}`),
        api.get<{ activity: Activity[] }>('/admin/activity?limit=14'),
        api.get<{ jobs: LiveJob[] }>('/admin/jobs?state=assigned&limit=8'),
      ]);
      setOverview(o); setFinance(f); setAnalytics(a);
      setActivity(act.activity); setLive(jobs.jobs ?? []);
      setError(null);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not load operations.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);
  useEffect(() => {
    const t = setInterval(() => { void load(); }, 30_000);
    return () => clearInterval(t);
  }, [load]);

  /**
   * Today against the same day last week.
   *
   * A number with nothing beside it cannot be read. "12 deliveries" is
   * meaningless until you know whether last Tuesday was 4 or 40 -- and the
   * previous *day* is the wrong comparison for a business whose weekends
   * differ from its weekdays.
   */
  const trend = useMemo(() => {
    const d = finance?.daily ?? [];
    if (d.length < 8) return null;
    const today = d[d.length - 1]!;
    const weekAgo = d[d.length - 8]!;
    const change = (now: number, then: number) =>
      then === 0 ? (now > 0 ? 100 : 0) : Math.round(((now - then) / then) * 100);
    return {
      deliveries: change(today.deliveries, weekAgo.deliveries),
      charged: change(Number(today.charged_minor), Number(weekAgo.charged_minor)),
    };
  }, [finance]);

  const primary = finance?.totals[0];
  const owed = overview?.owedToDrivers?.[0];
  const revenueToday = overview?.revenueToday;
  const alerts = [
    overview?.documents.awaitingReview
      ? { n: overview.documents.awaitingReview,
          text: 'documents awaiting review', sub: 'a driver cannot work until decided',
          href: '/document-review', tone: 'amber' as const }
      : null,
    overview?.unpaidDelivered
      ? { n: overview.unpaidDelivered, text: 'delivered but never charged',
          sub: 'completed work with no payment', href: '/jobs', tone: 'red' as const }
      : null,
    overview?.jobsToday.searching
      ? { n: overview.jobsToday.searching, text: 'looking for a driver',
          sub: 'nobody has accepted yet', href: '/jobs', tone: 'amber' as const }
      : null,
    overview?.jobsToday.failed
      ? { n: overview.jobsToday.failed, text: 'failed today',
          sub: 'could not be completed', href: '/jobs', tone: 'red' as const }
      : null,
  ].filter(Boolean) as { n: number; text: string; sub: string; href: string;
                         tone: 'amber' | 'red' }[];

  if (loading) {
    return <div className="p-6 text-sm text-muted-foreground">Loading operations…</div>;
  }

  return (
    <div className="space-y-3 p-4">
      {error && (
        <div role="alert"
          className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-600">
          {error} <button onClick={() => void load()} className="underline">Retry</button>
        </div>
      )}

      {/* Zone 1 — what needs a person, compact enough to read in one line each. */}
      {alerts.length > 0 ? (
        <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
          {alerts.map((a, i) => (
            <Link key={i} href={a.href}
              className={`flex items-center gap-2.5 rounded-lg border px-3 py-2 transition-colors ${
                a.tone === 'red'
                  ? 'border-red-200 bg-red-50 hover:bg-red-100'
                  : 'border-amber-200 bg-amber-50 hover:bg-amber-100'}`}>
              <span className={`text-xl font-700 tabular-nums ${
                a.tone === 'red' ? 'text-red-700' : 'text-amber-700'}`}>{a.n}</span>
              <span className="min-w-0">
                <span className={`block truncate text-[12.5px] font-600 ${
                  a.tone === 'red' ? 'text-red-800' : 'text-amber-900'}`}>{a.text}</span>
                <span className={`block truncate text-[11px] ${
                  a.tone === 'red' ? 'text-red-600' : 'text-amber-700'}`}>{a.sub}</span>
              </span>
            </Link>
          ))}
        </div>
      ) : (
        <div className="rounded-lg border border-green-200 bg-green-50 px-3 py-2 text-[13px] text-green-800">
          Nothing needs attention.
        </div>
      )}

      {/* Zone 2 — today, with something to compare it against. */}
      <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-6">
        <Kpi label="Deliveries today" value={String(overview?.jobsToday.total ?? 0)}
          delta={trend?.deliveries} sub={`${overview?.jobsToday.delivered ?? 0} completed`} />
        <Kpi label="Charged today"
          value={money(revenueToday?.totalMinor ?? revenueToday?.total_minor,
            revenueToday?.currencyCode)}
          delta={trend?.charged} sub="incl. tax and tips" />
        <Kpi label="Commission (14d)"
          value={money(primary?.commission_minor, primary?.currency_code)}
          sub="what Lejo kept" emphasis />
        <Kpi label="On the road" value={String(overview?.jobsToday.inFlight ?? 0)}
          sub={`${overview?.drivers.online ?? 0} drivers online`} />
        <Kpi label="Owed to drivers"
          value={money(owed?.totalMinor ?? owed?.total_minor, owed?.currencyCode)}
          sub="not yet paid out" />
        <Kpi label="Driver rating"
          value={analytics?.drivers.avg_rating
            ? Number(analytics.drivers.avg_rating).toFixed(2) : '—'}
          sub={analytics?.drivers.ratings
            ? `${analytics.drivers.ratings} ratings` : 'none yet'} />
      </div>

      {/* Zone 3 — the shape of the fortnight, and the shape of a day. */}
      <div className="grid gap-3 lg:grid-cols-3">
        <Fortnight daily={finance?.daily ?? []} currency={primary?.currency_code ?? ''} />
        <RushHours hours={analytics?.bookingsByHour ?? []} />
      </div>

      {/* Zone 4 — live work beside the event log, the way an operator works. */}
      <div className="grid gap-3 lg:grid-cols-2">
        <OnTheRoad jobs={live} />
        <Recent events={activity} />
      </div>
    </div>
  );
}

function Kpi({ label, value, sub, delta, emphasis }: {
  label: string; value: string; sub: string; delta?: number; emphasis?: boolean;
}) {
  return (
    <div className={`rounded-lg border p-2.5 ${
      emphasis ? 'border-primary/40 bg-primary/5' : 'border-border bg-card'}`}>
      <p className="truncate text-[10.5px] uppercase tracking-wide text-muted-foreground">
        {label}
      </p>
      <div className="mt-1 flex items-baseline gap-1.5">
        <span className="text-[19px] font-700 tabular-nums leading-none text-foreground">
          {value}
        </span>
        {delta !== undefined && delta !== 0 && (
          <span className={`text-[11px] font-600 tabular-nums ${
            delta > 0 ? 'text-green-600' : 'text-red-600'}`}>
            {delta > 0 ? '▲' : '▼'}{Math.abs(delta)}%
          </span>
        )}
      </div>
      <p className="mt-0.5 truncate text-[11px] text-muted-foreground">
        {sub}{delta !== undefined && <span className="ml-1">vs same day last week</span>}
      </p>
    </div>
  );
}

/** Fourteen days of deliveries, with the money behind them. */
function Fortnight({ daily, currency }: {
  daily: { day: string; deliveries: number; charged_minor: string;
           commission_minor: string }[];
  currency: string;
}) {
  const max = Math.max(1, ...daily.map(d => d.deliveries));
  const any = daily.some(d => d.deliveries > 0);
  const total = daily.reduce((n, d) => n + d.deliveries, 0);

  return (
    <div className="rounded-lg border border-border bg-card p-3 lg:col-span-2">
      <div className="flex items-baseline justify-between">
        <p className="text-[13px] font-600 text-foreground">Last 14 days</p>
        <p className="text-[11px] text-muted-foreground">
          {total} deliver{total === 1 ? 'y' : 'ies'}
        </p>
      </div>
      {!any ? (
        <p className="py-9 text-center text-[12px] text-muted-foreground">
          No deliveries in the last fortnight.
        </p>
      ) : (
        <>
          <div className="mt-2 flex h-24 items-end gap-1">
            {daily.map(d => (
              <div key={d.day} className="flex flex-1 flex-col justify-end"
                style={{ height: '100%' }}
                title={`${new Date(d.day).toLocaleDateString(undefined,
                  { weekday: 'short', day: 'numeric', month: 'short' })} — ${
                  d.deliveries} deliveries, ${money(d.charged_minor, currency)}`}>
                <div className="w-full rounded-t bg-primary/70 hover:bg-primary"
                  style={{ height: `${(d.deliveries / max) * 100}%`,
                           minHeight: d.deliveries > 0 ? '3px' : '0' }} />
              </div>
            ))}
          </div>
          <div className="mt-1 flex justify-between text-[10px] text-muted-foreground">
            <span>{new Date(daily[0]!.day).toLocaleDateString(undefined,
              { day: 'numeric', month: 'short' })}</span>
            <span>today</span>
          </div>
        </>
      )}
    </div>
  );
}

/**
 * When the work arrives.
 *
 * Busy-time pricing has been configurable since the first build and there has
 * never been anywhere to see the demand it should be written against.
 */
function RushHours({ hours }: { hours: { hour: number; deliveries: number }[] }) {
  const byHour = new Map(hours.map(h => [h.hour, h.deliveries]));
  const all = Array.from({ length: 24 }, (_, h) => byHour.get(h) ?? 0);
  const max = Math.max(1, ...all);
  const busiest = all.indexOf(max);
  const any = all.some(n => n > 0);

  return (
    <div className="rounded-lg border border-border bg-card p-3">
      <div className="flex items-baseline justify-between">
        <p className="text-[13px] font-600 text-foreground">Busy hours</p>
        {any && (
          <Link href="/pricing-surge-management"
            className="text-[11px] text-primary hover:underline">
            set surge
          </Link>
        )}
      </div>
      <p className="text-[11px] text-muted-foreground">
        {any ? `Busiest around ${busiest}:00` : 'No bookings yet'}
      </p>
      <div className="mt-2 flex h-24 items-end gap-[1.5px]">
        {all.map((n, h) => (
          <div key={h} className="flex flex-1 flex-col justify-end" style={{ height: '100%' }}
            title={`${h}:00 — ${n} booking${n === 1 ? '' : 's'}`}>
            <div className={`w-full rounded-t ${
              n === max && max > 0 ? 'bg-primary' : 'bg-primary/30'}`}
              style={{ height: `${(n / max) * 100}%` }} />
          </div>
        ))}
      </div>
      <div className="mt-1 flex justify-between text-[10px] text-muted-foreground">
        <span>00</span><span>06</span><span>12</span><span>18</span><span>23</span>
      </div>
    </div>
  );
}

function OnTheRoad({ jobs }: { jobs: LiveJob[] }) {
  return (
    <div className="rounded-lg border border-border bg-card">
      <div className="flex items-baseline justify-between border-b border-border px-3 py-2">
        <p className="text-[13px] font-600 text-foreground">Out for delivery</p>
        <Link href="/jobs" className="text-[11px] text-primary hover:underline">all jobs</Link>
      </div>
      {jobs.length === 0 ? (
        <p className="px-3 py-6 text-center text-[12px] text-muted-foreground">
          Nothing on the road. Jobs appear here once a driver accepts them.
        </p>
      ) : (
        <div className="divide-y divide-border">
          {jobs.map(j => (
            <Link key={j.id} href="/jobs"
              className="flex items-center justify-between gap-2 px-3 py-2 hover:bg-muted">
              <span className="min-w-0">
                <span className="block truncate text-[12.5px] font-600 text-foreground">
                  {j.reference}
                </span>
                <span className="block truncate text-[11px] text-muted-foreground">
                  {j.driver_name ?? 'unassigned'} · {j.state.replace(/_/g, ' ')}
                </span>
              </span>
              <span className="flex-shrink-0 text-[12px] tabular-nums text-foreground">
                {money(j.total_minor, j.currency_code)}
              </span>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

function Recent({ events }: { events: Activity[] }) {
  return (
    <div className="rounded-lg border border-border bg-card">
      <div className="flex items-baseline justify-between border-b border-border px-3 py-2">
        <p className="text-[13px] font-600 text-foreground">Recent activity</p>
        <Link href="/notification-feed"
          className="text-[11px] text-primary hover:underline">everything</Link>
      </div>
      {events.length === 0 ? (
        <p className="px-3 py-6 text-center text-[12px] text-muted-foreground">
          Nothing has happened yet.
        </p>
      ) : (
        <div className="divide-y divide-border">
          {events.map((e, i) => (
            <div key={i} className="flex items-center justify-between gap-2 px-3 py-1.5">
              <span className="min-w-0">
                <span className="block truncate text-[12.5px] text-foreground">
                  <span className="font-600 capitalize">
                    {e.detail.replace(/_/g, ' ')}
                  </span>
                  {' · '}{e.reference}
                </span>
                <span className="block truncate text-[11px] text-muted-foreground">
                  {e.who}
                </span>
              </span>
              <span className="flex-shrink-0 text-[11px] tabular-nums text-muted-foreground"
                title={new Date(e.at).toLocaleString()}>
                {ago(e.at)}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
